<template>
  <h1 data-testid="viewer-item-name" class="mx-4 mt-1 pl-1 sm:mx-0 sm:text-2xl dark:text-zinc-300 input-wrapper">
    <span class="sr-only">Image file with a title:</span>
    <ZtTooltip content="Rename" :disabled="!editEnabled">
      <ContentEditable
        class="editable"
        data-testid="viewer-item-name-editable"
        :class="{ errored: !isValid, saving: isSaving }"
        @focus="onFocus"
        tag="span"
        :contenteditable="editEnabled"
        :spellcheck="false"
        v-model="name"
        :no-nl="true"
        :no-html="true"
        @keyup="validate"
        @returned="onSave"
        @blur="onBlur"
      />
    </ZtTooltip>
  </h1>
</template>

<script lang="ts" setup>
import { Ref, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { ZtTooltip, ContentEditable } from '@/storybook';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useToast } from '@/composables/useToast';
import { useUserStore } from '@/store/useUserStore';
import { TrackClickEvents, useTrack } from '@/composables/useTrack';

const MAX_TITLE_LENGTH = 20000;

const userStore = useUserStore();
const itemStore = useViewerItemStore();
const { trackClick } = useTrack();

const { userId, displayName } = storeToRefs(itemStore);
const { id: ownerId } = storeToRefs(userStore);

const { error } = useToast();

const name: Ref<string> = ref(displayName.value || '');
const isSaving: Ref<boolean> = ref(false);
const isValid: Ref<boolean> = ref(true);
const editEnabled: Ref<boolean> = ref(ownerId?.value === userId.value); // enabled only for owners

const validate = () => {
  const val = name.value.trim();
  isValid.value = val.length > 0 && val.length < MAX_TITLE_LENGTH;
};

const onBlur = () => {
  validate();
  if (!isValid.value) {
    name.value = displayName.value || '';
    return;
  }

  onSave();
};

watch(displayName, () => {
  if (isSaving.value) {
    return;
  }

  name.value = displayName.value || '';
});

const onFocus = () => trackClick(TrackClickEvents.ITEM_NAME_EDIT);

const onSave = () => {
  validate();
  if (!isValid.value || isSaving.value) {
    return;
  }

  if (displayName.value === name.value) {
    return;
  }

  isSaving.value = true;

  itemStore
    .updateDisplayName(name.value.trim())
    .then(() => {
      isSaving.value = false;
      if (document) {
        // @ts-ignore
        document.activeElement.blur();
      }
    })
    .catch((e: Error) => {
      error(e.message);
      isSaving.value = false;
    });
};
</script>

<style scoped>
.editable[contenteditable='true'] {
  @apply inline-block w-full outline-none;
}

.editable[contenteditable='true']:hover,
.editable[contenteditable='true']:focus-visible {
  @apply -my-1 -mx-1 px-1 py-1 rounded-10 bg-slate-100 dark:bg-zinc-800;
}

/* when editing */
.editable[contenteditable='true']:focus-visible {
  @apply ring ring-blue-500/50;
}
</style>
